import { useCallback, useMemo, useState, useEffect } from "react";
import { Button, Dropdown, Form, Header, Icon, Input, Label, Menu, Message, Segment, Statistic } from "semantic-ui-react";
import { createColumnHelper } from "@tanstack/react-table";
import { bookingApi, dashboardApi, ghApi } from "../../api/api";
import { mapBooking } from "../../api/mappers";
import { useBooking } from "../../context/BookingContext";
import StatusBadge from "./StatusBadge";
import { formatRupees } from "../../utils/money";
import DataTable from "./DataTable";
import RoomNumbersCell from "./RoomNumbersCell";
import DepartureCell, { ArrivalCell } from "./DepartureCell";
import LastUpdatedBadge from "./LastUpdatedBadge";
import {
  allGuestNames,
  bookingArrival,
  bookingDeparture,
  buildSearchHaystack,
  compareByArrival,
  compareByDeparture,
  fmtDate,
  fmtDateTime,
  fmtDateAndTime,
  bookingRowStyle,
  getActualCheckInOut,
  dateKeyOf,
  dayStr,
  todayStr,
  truncate,
  matchesSearch,
  roomLabel,
  roomOccupancyState,
  shortBookingType,
  STATUSES_WITH_ASSIGNED_ROOM,
} from "./adminHelpers";

const columnHelper = createColumnHelper();

/* Free-text cells past this many characters are ellipsised (full value on
   hover) — long enough for the great majority of real names, departments and
   guest-house names to survive intact, short enough that an outlier can't
   stretch its column for every other row. */
const CELL_TEXT_MAX = 20;

/* A remark is a sentence rather than a name, so it gets a longer leash than
   the other free-text columns before it is ellipsised. */
const REMARK_TEXT_MAX = 40;

/* Several guests' names share one cell on the Checked In tile. */
const GUEST_NAMES_TEXT_MAX = 40;

/* Renders one capped free-text value: em-dash when empty, otherwise the
   truncated text with the full value on hover. Sorting and the search index
   below both still run on the untruncated value. `title` is separate from the
   text for the Remarks column, whose hover says who wrote the note and when
   as well as what it said. */
const capped = (text, max = CELL_TEXT_MAX, title = text) =>
  text ? <span title={title}>{truncate(text, max)}</span> : "—";

const cappedCell = (info) => capped(info.getValue());

/* The statuses that get their own headline tile, in display order. Everything
   else (Partial, Rejected, No Show) is folded into the trailing
   "Other" tile so the row always adds up to Total — and each of those is still
   individually reachable via the Filters status dropdown below. */
const HEADLINE_STATUSES = ["Pending", "Approved", "Checked In", "Checked Out", "Cancelled", "Transferred"];
const OTHER_KEY = "Other";
const HEADLINE_STAT_KEYS = ["All", ...HEADLINE_STATUSES, OTHER_KEY];

/* What the Transferred tile lists. A guest house admin's is what it sent away
   (the API shows those as status Transferred). The super admin sends from
   nowhere, so theirs is every booking ever transferred, each under its real
   status — which is why that tile sits outside the add-up-to-Total sum. */
const inTransferredTile = (b, isSuperAdmin) =>
  isSuperAdmin ? b.wasTransferred : b.status === "Transferred";

/* The "Rooms Requested" cell for a Pending booking — its top guest house
   preference plus the room types asked for, each by its short name when the
   room type has one. Shared by the column and the search index so the two
   can't describe the same booking differently. */
function roomsRequestedText(row, ghShortById) {
  const pref = row.guestHousePreferenceOptions?.[0];
  if (!pref) return "";
  const ghLabel = ghShortById?.get(pref.id) || pref.name;
  const reqStr = (pref.roomRequests || [])
    .map((r) => {
      const typeName = r.roomTypeShortName || r.roomTypeName;
      return r.count > 1 ? `${r.count} x ${typeName}` : typeName;
    })
    .join(", ");
  if (ghLabel && reqStr) return `${ghLabel} (${reqStr})`;
  return ghLabel || reqStr;
}

/* The "Room No" cell — every room the booking actually holds, comma-joined,
   since one booking can span several rooms. Shared by the column and the
   search index for the same reason as roomsRequestedText above. Bookings
   whose status doesn't hold a room (Pending, Rejected, ...) have nothing to
   show, and the column is dropped outright under the Pending tile. */
function assignedRoomsText(row) {
  if (!STATUSES_WITH_ASSIGNED_ROOM.includes(row.status)) return "";
  return (row.roomAssignments || [])
    .filter((a) => a.roomNumber)
    .map((a) => roomLabel(a.roomNumber, a.guestHouseShortName))
    .join(", ");
}

/* The house(s) actually holding a room, comma-joined — not the house the indent
   was filed with, which is what assigned_gh stays pinned to even when the rooms
   come from somewhere else. Gated on the same statuses as the Room No column
   beside it, so the two can't describe the same booking differently. */
function assignedGuestHouseText(row, ghShortById) {
  if (!STATUSES_WITH_ASSIGNED_ROOM.includes(row.status)) return "";
  const ids = row.stayGuestHouseIds || [];
  const names = row.stayGuestHouseNames || [];
  return names.map((name, i) => ghShortById?.get(ids[i]) || name).join(", ");
}

/* The same rooms as words, for the search index: typing "awaiting" finds every
   booking still expecting a guest, which is the question the Room No column
   below now answers visually. */
function roomStateSearchText(row) {
  if (!STATUSES_WITH_ASSIGNED_ROOM.includes(row.status)) return "";
  return (row.roomAssignments || [])
    .filter((a) => a.roomNumber)
    .map((a) => `${a.roomNumber} ${roomOccupancyState(a).text}`)
    .join(" ");
}

/* The newest note on a booking, or "" when nobody has written one. Shared by
   the Remarks column and the search index so the two can't disagree, and kept
   as the column's accessor so sorting runs on the text the reader sees. */
function latestRemarkText(row) {
  return row.latestRemark?.text || "";
}

/* The Remarks cell's hover: the note in full, then who left it and when — a
   note reads quite differently depending on which of those it came from. */
function remarkTitle(row) {
  const r = row.latestRemark;
  if (!r) return "";
  const who = r.created_by_name || r.actor_label || "System";
  return `${r.text}\n— ${who}, ${fmtDateTime(r.created_at)}`;
}

/* The date the Yesterday/Today/Tomorrow chips filter on, which follows the
   selected stat tile the same way the table's own date columns do: under
   Checked In/Checked Out that's the actual check-in/checkout the reader can
   see in the row, everywhere else the Arrival column's date (bookingArrival —
   the room's own start when it was moved). Filtering on a date the view
   doesn't show would make the chip look broken. */
function dayKeyFor(row, activeKey) {
  if (activeKey === "Checked In") return dateKeyOf(getActualCheckInOut(row).checkin);
  if (activeKey === "Checked Out") return dateKeyOf(getActualCheckInOut(row).checkout);
  return bookingArrival(row).date || null;
}

/* An actual check-in/checkout can't be in the future, so "Tomorrow" would
   always come back empty under those two tiles. */
const ACTUALS_KEYS = ["Checked In", "Checked Out"];

const DAY_FIELD_LABEL = {
  "Checked In": "Check-in",
  "Checked Out": "Checkout",
};

const BOOKING_TYPE_OPTIONS = [
  { value: "All", label: "All" },
  { value: "Official", label: "Official" },
  { value: "Non-official", label: "Non-Official" },
];

/* The month picker scopes the whole screen, and it does it on the server:
   picking a month refetches both the rows and the headline counts rather than
   sieving a copy of every booking in the system. Which months it offers comes
   from the server too (bookings/months/), so the list ends where the bookings
   do instead of guessing a window. */
const ALL_TIME = "all";
/* Only used until that call answers, and as the standing fallback if it fails
   — a picker that offers nothing would strand the user on one month. */
const MONTHS_AHEAD = 3;
const MONTHS_BACK = 12;

/* Built from local getFullYear/getMonth rather than toISOString, for the same
   reason localDateKey is in adminHelpers: an IST evening is already the next
   day in UTC, which would name the wrong month on the 31st. */
const monthValue = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

const monthLabel = (value) => {
  const [year, month] = value.split("-").map(Number);
  return new Date(year, month - 1, 1).toLocaleDateString("en-IN", {
    month: "short",
    year: "numeric",
  });
};

/* Newest first, matching the order the server returns. */
function buildFallbackMonths() {
  const now = new Date();
  const months = [];
  for (let offset = MONTHS_AHEAD; offset >= -MONTHS_BACK; offset -= 1) {
    months.push(monthValue(new Date(now.getFullYear(), now.getMonth() + offset, 1)));
  }
  return months;
}

function MonthDropdown({ value, options, onChange }) {
  const selected = options.find((o) => o.value === value) || options[0];

  return (
    <Dropdown
      button
      className="icon compact"
      icon="chevron down"
      /* `trigger`, not `text`: Semantic UI types `text` as a string, so
         passing this icon+label fragment there logged a propType error, left
         the visible label unclickable, and hid it after a selection. */
      trigger={
        <span>
          <Icon name="calendar outline" />
          {selected.label}
        </span>
      }
    >
      <Dropdown.Menu>
        {options.map((o) => (
          <Dropdown.Item
            key={o.value}
            active={o.value === value}
            icon={o.value === value ? "check" : undefined}
            text={o.label}
            onClick={() => onChange(o.value)}
          />
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
}

export default function SummaryTab({
  isSuperAdmin = false,
  bookings,
  rooms,
  guestHouses,
  onGhClick,
  onGhBookedClick,
  onOpenDetail,
  /* Lifted to the parent (rather than local useState) so the selected stat
     survives opening/closing the booking detail view — AdminDashboard swaps
     SummaryTab out of the tree entirely while the detail modal is open,
     which would otherwise reset any local state back to its default. Now
     backed by the URL (TODO #9) rather than plain parent state, along with
     every other filter below — see useAdminUrlState. */
  activeKey,
  onActiveKeyChange: setActiveKey,
  month: monthProp,
  bookingTypeFilter = "All",
  /* Holds the picked day itself ("YYYY-MM-DD"), not "today"/"tomorrow", so a
     tab left open across midnight keeps showing the day that was clicked
     instead of silently sliding onto the next one. */
  dayFilter = "",
  colArrival = "",
  colDeparture = "",
  searchQuery = "",
  onFilterChange,
}) {
  /* The current month is the default view — the dashboard opens on the work
     in front of you, not on every booking ever taken. */
  const month = monthProp || monthValue(new Date());
  const [monthList, setMonthList] = useState(buildFallbackMonths);
  const [monthBookings, setMonthBookings] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [apiSummary, setApiSummary] = useState(null);
  /* True on first paint only. Both loading flags are raised by the actions
     that leave the screen with nothing to show — the first load and a month
     switch — never by the background refresh below, which swaps the new data
     in silently. Flashing "…" over the tiles and a dimmer over the table on
     every poll made the page look like it was reloading itself. */
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [filterOpen, setFilterOpen] = useState(false);
  const [listSearch, setListSearch] = useState(searchQuery);
  /* What the list actually filters on — a prop now, held a beat behind the
     input via the debounce below; see the matching note in BookingsTab.
     Rebuilding the list re-anchors it on today, which mid-word is a flicker. */
  const listQuery = searchQuery;
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");
  const { lastSyncedAt, apiLoading, paymentResponsibilityOptions } = useBooking();

  /* Normalize the URL to carry an explicit month once resolved, so a shared
     link reproduces exactly what's on screen instead of "whichever month is
     current when it happens to be opened". */
  useEffect(() => {
    if (!monthProp) onFilterChange({ month });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentMonth = monthValue(new Date());

  const monthOptions = useMemo(
    () => [
      { value: ALL_TIME, label: "All Time" },
      ...monthList.map((m) => ({ value: m, label: monthLabel(m) })),
    ],
    [monthList],
  );

  /* Where the arrows can go from here. Both come out undefined on All Time,
     which has no neighbours, and at either end of the list. */
  const monthIndex = monthList.indexOf(month);
  const olderMonth = monthIndex >= 0 ? monthList[monthIndex + 1] : undefined;
  const newerMonth = monthIndex > 0 ? monthList[monthIndex - 1] : undefined;

  /* Which months exist is a property of the data, so it's asked for once and
     refreshed with it — not regenerated from today's date on every mount. */
  useEffect(() => {
    let cancelled = false;
    bookingApi
      .months()
      .then((data) => {
        const months = data?.months || [];
        if (!cancelled && months.length) setMonthList(months);
      })
      .catch(() => {
        /* Older backend or a failed call — the generated span still works. */
      });
    return () => {
      cancelled = true;
    };
  }, [bookings]);

  /* Headline counts are aggregated server-side, under the same month the list
     below uses, so the tiles always cover every booking in scope rather than
     whatever this tab happens to be holding.
     Keyed on `bookings` rather than lastSyncedAt: the heartbeat restamps that
     every 8s even when nothing changed, whereas the context only replaces the
     bookings array when a reload actually brought something new. */
  useEffect(() => {
    let cancelled = false;
    dashboardApi
      .summary(month === ALL_TIME ? {} : { month })
      .then((data) => {
        if (!cancelled) setApiSummary(data);
      })
      .catch(() => {
        if (!cancelled) setApiSummary(null);
      })
      .finally(() => {
        if (!cancelled) setSummaryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [month, bookings]);

  /* All Time reads the context's dataset, which is already loaded and kept
     fresh by the heartbeat. A specific month is fetched scoped instead, so
     this screen doesn't need every booking in the system in the browser. */
  useEffect(() => {
    if (month === ALL_TIME) {
      setMonthBookings([]);
      /* Switching back mid-flight cancels the pending fetch, whose own
         finally() is then skipped — without this the dimmer would never lift. */
      setListLoading(false);
      return undefined;
    }
    /* Guards against a slow response for a month the user has already moved
       off landing on top of the newer one. */
    let cancelled = false;
    bookingApi
      .list({ month })
      .then((rows) => {
        if (!cancelled) {
          setMonthBookings((rows || []).map((b) => mapBooking(b, paymentResponsibilityOptions)));
        }
      })
      .catch(() => {
        if (!cancelled) setMonthBookings([]);
      })
      .finally(() => {
        if (!cancelled) setListLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [month, bookings, paymentResponsibilityOptions]);

  const filteredBookings = month === ALL_TIME ? bookings : monthBookings;

  /* Counts come from the API whenever it answered — it aggregates over the
     same month, so there's no longer an "all time only" caveat; the local
     tally is the fallback for a failed summary call.
     `other` is derived by subtraction rather than by summing the leftover
     statuses, so the headline row reconciles with Total even if the backend
     grows a status this screen doesn't know about yet. */
  const counts = useMemo(() => {
    const withOther = (c) => ({
      ...c,
      other: Math.max(
        0,
        c.total - (c.pending + c.confirmed + c.checkedIn + c.checkedOut + c.cancelled + c.transferredStatus),
      ),
    });

    if (apiSummary) {
      const s = apiSummary.by_status || {};
      return withOther({
        total: apiSummary.total ?? 0,
        pending: s.PENDING ?? 0,
        confirmed: s.CONFIRMED ?? 0,
        partial: s.PARTIALLY_CONFIRMED ?? 0,
        rejected: s.REJECTED ?? 0,
        cancelled: s.CANCELLED ?? 0,
        transferred: apiSummary.transferred ?? s.TRANSFERRED ?? 0,
        transferredStatus: s.TRANSFERRED ?? 0,
        checkedIn: s.CHECKED_IN ?? 0,
        checkedOut: s.CHECKED_OUT ?? 0,
        noShow: s.NO_SHOW ?? 0,
      });
    }
    return withOther({
      total: filteredBookings.length,
      pending: filteredBookings.filter((b) => b.status === "Pending").length,
      confirmed: filteredBookings.filter((b) => b.status === "Approved").length,
      partial: filteredBookings.filter(
        (b) => b.status === "Partially Confirmed",
      ).length,
      rejected: filteredBookings.filter((b) => b.status === "Rejected").length,
      cancelled: filteredBookings.filter((b) => b.status === "Cancelled")
        .length,
      transferred: filteredBookings.filter((b) => inTransferredTile(b, isSuperAdmin)).length,
      transferredStatus: filteredBookings.filter((b) => b.status === "Transferred").length,
      checkedIn: filteredBookings.filter((b) => b.status === "Checked In").length,
      checkedOut: filteredBookings.filter((b) => b.status === "Checked Out").length,
      noShow: filteredBookings.filter((b) => b.status === "No Show").length,
    });
  }, [filteredBookings, apiSummary, isSuperAdmin]);

  /* One searchable blob per booking, covering every column the table can
     render. Built once per dataset rather than inside the filter, so typing
     doesn't re-derive it for every row on every keystroke. */
  const searchIndex = useMemo(() => {
    const index = new Map();
    for (const b of filteredBookings) {
      const { checkin, checkout } = getActualCheckInOut(b);
      const departure = bookingDeparture(b);
      const arrival = bookingArrival(b);
      index.set(
        b.id,
        buildSearchHaystack([
          b.bookingCode,
          b.employeeId,
          b.department,
          b.applicantName,
          /* Every room's guest, not just the lead one. */
          allGuestNames(b).join(", "),
          b.bookingType,
          shortBookingType(b),
          /* Both senses of "guest house": the house the indent was filed with
             and the house(s) whose rooms it holds, so a booking raised for one
             and given a room in another is found by either name. Not the gated
             helper above — a cancelled booking stays findable by its house. */
          b.guestHouseName,
          (b.stayGuestHouseNames || []).join(", "),
          b.status,
          roomsRequestedText(b),
          /* The column shows a room type's short name; its full name is
             still what a reader is likely to type. */
          (b.guestHousePreferenceOptions?.[0]?.roomRequests || []).map((r) => r.roomTypeName).join(" "),
          assignedRoomsText(b),
          roomStateSearchText(b),
          /* Not on screen as text — the globe stands for it. */
          b.isExternal && "namaste",
          /* Dates go in twice: raw for "2026-09-08", formatted for the
             "8 Sept 2026" the reader actually sees on screen. The booked
             departure stays findable after the column moves to a changed one. */
          b.checkIn,
          fmtDateAndTime(b.checkIn, b.arrivalTime),
          arrival.date,
          fmtDateAndTime(arrival.date, arrival.time),
          b.checkOut,
          fmtDateAndTime(b.checkOut, b.departureTime),
          departure.date,
          fmtDateAndTime(departure.date, departure.time),
          latestRemarkText(b),
          checkin,
          fmtDateTime(checkin),
          checkout,
          fmtDateTime(checkout),
        ]),
      );
    }
    return index;
  }, [filteredBookings]);

  const listBookings = useMemo(() => {
    /* Every whitespace-separated term has to match (AND), so "parimal pending"
       narrows instead of finding nothing; punctuation is ignored, so
       "ak sharma" finds "A.k.sharma". */
    const list = filteredBookings.filter((b) => {
      if (activeKey === OTHER_KEY) {
        if (HEADLINE_STATUSES.includes(b.status)) return false;
      } else if (activeKey === "Transferred") {
        if (!inTransferredTile(b, isSuperAdmin)) return false;
      } else if (activeKey !== "All" && b.status !== activeKey) return false;
      if (bookingTypeFilter !== "All" && b.bookingType !== bookingTypeFilter) return false;
      if (dayFilter && dayKeyFor(b, activeKey) !== dayFilter) return false;
      if (colArrival && bookingArrival(b).date !== colArrival) return false;
      if (colDeparture && bookingDeparture(b).date !== colDeparture) return false;
      if (listQuery.trim() && !matchesSearch(searchIndex.get(b.id) || "", listQuery)) return false;
      return true;
    });
    return [...list].sort(compareByArrival);
  }, [filteredBookings, activeKey, isSuperAdmin, bookingTypeFilter, dayFilter, colArrival, colDeparture, listQuery, searchIndex]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (listSearch !== searchQuery) onFilterChange({ sQuery: listSearch });
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listSearch]);

  useEffect(() => {
    setListSearch(searchQuery);
  }, [searchQuery]);

  /* Open the list at today rather than at the earliest arrival in the month;
     earlier ones load as the reader scrolls up into them. */
  const firstCurrentArrival = useCallback((b) => (bookingArrival(b).date || "") >= todayStr(), []);

  /* Rebuilt every render rather than memoised, so the labels can't outlive the
     day they were computed on. A dayFilter picked before midnight then matches
     none of them — it keeps its own chip so the list never looks filtered by
     nothing. */
  const dayOptions = [
    { value: dayStr(-1), label: "Yesterday" },
    { value: todayStr(), label: "Today" },
    {
      value: dayStr(1),
      label: "Tomorrow",
      disabled: ACTUALS_KEYS.includes(activeKey),
      disabledHint: `A ${(DAY_FIELD_LABEL[activeKey] || "").toLowerCase()} can't be in the future.`,
    },
  ];
  if (dayFilter && !dayOptions.some((o) => o.value === dayFilter)) {
    dayOptions.push({ value: dayFilter, label: fmtDate(dayFilter) });
  }

  const stats = [
    {
      key: "All",
      label: "Total",
      value: counts.total,
      icon: "list alternate outline",
    },
    {
      key: "Pending",
      label: "Pending",
      value: counts.pending,
      icon: "clock outline",
    },
    {
      key: "Approved",
      label: "Confirmed",
      value: counts.confirmed,
      icon: "check circle outline",
    },
    {
      key: "Partially Confirmed",
      label: "Partial",
      value: counts.partial,
      icon: "adjust",
    },
    {
      key: "Checked In",
      label: "Checked In",
      value: counts.checkedIn,
      icon: "sign in",
    },
    {
      key: "Checked Out",
      label: "Checked Out",
      value: counts.checkedOut,
      icon: "sign out",
    },
    {
      key: "No Show",
      label: "No Show",
      value: counts.noShow,
      icon: "user times",
    },
    {
      key: "Rejected",
      label: "Rejected",
      value: counts.rejected,
      icon: "times circle outline",
    },
    {
      key: "Cancelled",
      label: "Cancelled",
      value: counts.cancelled,
      icon: "ban",
    },
    {
      key: "Transferred",
      label: "Transferred",
      value: counts.transferred,
      icon: "exchange",
    },
    {
      key: OTHER_KEY,
      label: "Other",
      value: counts.other,
      icon: "ellipsis horizontal",
    },
  ];

  const activeLabel = stats.find((s) => s.key === activeKey)?.label || activeKey;

  /* Clears the filters only — the selected stat tile is the view you're in,
     not a filter to undo, so Reset leaves you on Checked Out (say) with its
     chips cleared. Search is included: Reset sits beside the search box, so
     leaving a typed term behind would look broken. */
  /* The day chips name days in the current month; carrying one across a
     month switch would filter the new month's rows down to nothing. */
  const handleMonthChange = (value) => {
    if (value === month) return;
    onFilterChange({ month: value, day: "" });
    /* Drop the outgoing month's counts rather than leave them sitting under
       the new month's label while its own fetch is in flight. */
    setApiSummary(null);
    setSummaryLoading(true);
    if (value !== ALL_TIME) setListLoading(true);
  };

  const clearListFilters = () => {
    onFilterChange({ btype: "All", day: "", colArrival: "", colDeparture: "", sQuery: "" });
    setListSearch("");
  };

  /* The Filters panel's own Clear Filters does reset the status too, since
     Status is one of the fields inside that panel. */
  const resetFilters = () => {
    setActiveKey("All");
    clearListFilters();
  };

  const listFilterCount = [
    bookingTypeFilter !== "All",
    dayFilter !== "",
    colArrival !== "",
    colDeparture !== "",
    listSearch !== "",
  ].filter(Boolean).length;

  const activeFilterCount = listFilterCount + (activeKey !== "All" ? 1 : 0);

  /* Deliberately without listSearch: the column set itself changes with
     activeKey (Pending drops the Rooms column), and the remount is what keeps
     a sort on a since-removed column from lingering. Typing needs no remount —
     DataTable re-seeds its own window when the rows change. */
  const filterKey = [month, activeKey, bookingTypeFilter, dayFilter, colArrival, colDeparture].join("|");

  /* Switching to/from Checked In/Checked Out moves the chips onto a different
     date field, and "Tomorrow" has no meaning on the actuals — drop the
     selection rather than leave the list mysteriously empty. */
  const selectStatus = (key) => {
    if (dayFilter && ACTUALS_KEYS.includes(key) && dayFilter > todayStr()) onFilterChange({ day: "" });
    setActiveKey(key);
  };

  /* The daily occupancy sheet. Deliberately independent of everything on this
     screen — it always covers today and tomorrow across every room, whatever
     month, status or search the table happens to be showing. */
  const handleExport = async (variant) => {
    if (exporting) return;
    setExporting(true);
    setExportError("");
    try {
      const { blob, filename } = await ghApi.downloadOccupancyList(variant);
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      setExportError(err.message || "Failed to download the occupancy list.");
    } finally {
      setExporting(false);
    }
  };

  /* Guest house id -> short code, for the derived text builders below —
     shows "LDE" instead of "L D Guest House (Extension)" once a house has one
     configured, falling back to the full name otherwise. */
  const ghShortById = useMemo(
    () => new Map(guestHouses.map((g) => [g.id, g.shortName])),
    [guestHouses],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor("bookingCode", {
        header: "Booking No",
        /* Every code is the same short width, so the column only needs room
           for its own header — the space it would otherwise be handed goes to
           the columns holding names and departments, which are the ones
           actually truncating. */
        meta: { shrink: true },
        /* An indent from the external booking server carries a globe at the
           cell's right edge, so the globes line up whatever the code's width.
           Semantic's icon is 1em tall but inherits the table's taller line
           height, which drops the glyph below centre; lineHeight 1 keeps it in
           its box so the flex row can centre it against the text. */
        cell: (info) => (
          <span style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6, whiteSpace: "nowrap" }}>
            <span>{info.getValue()}</span>
            {info.row.original.isExternal && (
              <Icon
                name="globe"
                color="grey"
                fitted
                style={{ lineHeight: 1 }}
                title="From Namaste BHU (external booking server)"
              />
            )}
          </span>
        ),
      }),
      /* Checked In lists every room's guest, since that is who the desk is
         looking for; elsewhere the lead guest is enough. */
      ...(activeKey === "Checked In"
        ? [
            columnHelper.accessor((row) => allGuestNames(row).join(", "), {
              id: "guestName",
              header: "Guest Names",
              cell: (info) => capped(info.getValue(), GUEST_NAMES_TEXT_MAX),
            }),
          ]
        : [
            columnHelper.accessor("guestName", {
              header: "Guest Name",
              cell: cappedCell,
            }),
          ]),
      ...(activeKey === "Checked In"
        ? [
            columnHelper.accessor((row) => getActualCheckInOut(row).checkin, {
              id: "actualCheckIn",
              header: "Check-in Time",
              cell: (info) => fmtDateTime(info.getValue()),
              meta: { highlight: true },
            }),
            columnHelper.accessor((b) => bookingDeparture(b).key, {
              id: "checkOut",
              header: "Departure",
              cell: (info) => <DepartureCell booking={info.row.original} />,
              sortingFn: (rowA, rowB) => compareByDeparture(rowA.original, rowB.original),
            }),
          ]
        : activeKey === "Checked Out"
        ? [
            columnHelper.accessor((row) => getActualCheckInOut(row).checkin, {
              id: "actualCheckIn",
              header: "Check-in",
              cell: (info) => fmtDateTime(info.getValue()),
              meta: { highlight: true },
            }),
            columnHelper.accessor((row) => getActualCheckInOut(row).checkout, {
              id: "actualCheckOut",
              header: "Checkout",
              cell: (info) => fmtDateTime(info.getValue()),
              meta: { highlight: true },
            }),
          ]
        : [
            columnHelper.accessor((b) => bookingArrival(b).key, {
              id: "checkIn",
              header: "Arrival",
              /* The room's own start once it was moved, orange with the
                 booked arrival on hover — the mirror of Departure beside it. */
              cell: (info) => <ArrivalCell booking={info.row.original} />,
              /* Sorting on the same key the default order uses means clicking
                 "Arrival" ascending reproduces the opening list exactly. */
              sortingFn: (rowA, rowB) => compareByArrival(rowA.original, rowB.original),
            }),
            columnHelper.accessor((b) => bookingDeparture(b).key, {
              id: "checkOut",
              header: "Departure",
              cell: (info) => <DepartureCell booking={info.row.original} />,
              sortingFn: (rowA, rowB) => compareByDeparture(rowA.original, rowB.original),
            }),
          ]),
      /* A Pending booking has no rooms yet — it gets "Rooms Requested" below
         instead, so the column is dropped rather than left empty. */
      ...(activeKey === "Pending"
        ? []
        : [
            columnHelper.accessor(assignedRoomsText, {
              id: "roomNumbers",
              header: "Room No",
              cell: (info) => <RoomNumbersCell booking={info.row.original} />,
            }),
          ]),
      columnHelper.accessor("department", {
        header: "Department",
        cell: cappedCell,
      }),
      /* Accessor stays the full label so sorting and the Type filter agree
         with it; only the cell is abbreviated. */
      columnHelper.accessor("bookingType", {
        header: "B. Type",
        cell: (info) => shortBookingType(info.row.original) || "—",
      }),
      columnHelper.accessor("applicantName", {
        header: "Applicant Name",
        cell: cappedCell,
      }),
      ...(activeKey === "Pending"
        ? [
            columnHelper.accessor((row) => roomsRequestedText(row, ghShortById), {
              id: "roomsRequested",
              header: "Rooms Requested",
              cell: (info) => info.getValue() || "—",
            }),
            /* Advance taken while the request waits — sorts by amount. */
            columnHelper.accessor((row) => (row.advancePaid == null ? -1 : Number(row.advancePaid)), {
              id: "advancePaid",
              header: "Advance",
              cell: (info) => {
                const v = info.row.original.advancePaid;
                return v == null ? "—" : <strong>{formatRupees(v)}</strong>;
              },
            }),
          ]
        : [
            /* An accessor rather than a display column, so sorting and the
               search index key off the same text the column shows. */
            columnHelper.accessor((row) => assignedGuestHouseText(row, ghShortById), {
              id: "assignedGh",
              header: "Assign GH",
              /* Blank rather than an em dash for a status that never held a
                 room, the way this column has always read for those rows. */
              cell: (info) => (info.getValue() ? capped(info.getValue()) : ""),
            }),
          ]),
      columnHelper.accessor("status", {
        header: "Status",
        cell: (info) => <StatusBadge status={info.getValue()} />,
      }),
      /* The newest internal note: when a booking is worth asking about, the
         last thing someone wrote on it says more than the minute it was
         raised. Only the latest — the whole thread is a click away in the
         booking itself. Created At sits beside it rather than replacing it. */
      columnHelper.accessor(latestRemarkText, {
        id: "latestRemark",
        header: "Remarks",
        cell: (info) => capped(info.getValue(), REMARK_TEXT_MAX, remarkTitle(info.row.original)),
      }),
      columnHelper.accessor("createdAt", {
        header: "Created",
        cell: (info) => fmtDateTime(info.getValue()),
      }),
    ],
    [activeKey, ghShortById],
  );

  const rowStyleFor = (booking) => bookingRowStyle(booking);

  return (
    <div>
      {/* Headline stats — single line (API-powered for All Time). The sync
          badge and date range sit on this same line rather than in a heading
          row of their own: the tiles already name themselves, so a separate
          "Booking Status" caption only cost vertical space. */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12,
          marginBottom: 6,
        }}
      >
        <Statistic.Group widths={8} size="small" className="bhu-stat-row">
          {HEADLINE_STAT_KEYS.map((key) => {
            const s = stats.find((st) => st.key === key);
            if (!s) return null;
            return (
              <Statistic
                key={s.key}
                as="a"
                color={activeKey === s.key ? "blue" : undefined}
                onClick={() => selectStatus(s.key)}
                title={`Show ${s.label} bookings`}
                style={{ cursor: "pointer" }}
              >
                <Statistic.Value>
                  {summaryLoading ? "…" : s.value}
                </Statistic.Value>
                <Statistic.Label>{s.label}</Statistic.Label>
              </Statistic>
            );
          })}
        </Statistic.Group>
        <div
          style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}
        >
          <LastUpdatedBadge lastSyncedAt={lastSyncedAt} syncing={apiLoading} />
          {/* Arrows step one month at a time through the same list the menu
              offers — the common move is "the month either side of this one",
              which shouldn't cost opening a dropdown. Left is older. Spaced
              rather than a Button.Group: attached, the three read as one
              segmented control instead of three separate things to click. */}
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <Button
              icon="chevron left"
              size="small"
              disabled={!olderMonth}
              onClick={() => handleMonthChange(olderMonth)}
              title={olderMonth ? `Show ${monthLabel(olderMonth)}` : "No earlier month"}
            />
            <MonthDropdown
              value={month}
              options={monthOptions}
              onChange={handleMonthChange}
            />
            <Button
              icon="chevron right"
              size="small"
              disabled={!newerMonth}
              onClick={() => handleMonthChange(newerMonth)}
              title={newerMonth ? `Show ${monthLabel(newerMonth)}` : "No later month"}
            />
          </div>
        </div>
      </div>

      {/* Inline booking list for the selected stat — stays on the home screen */}
      <div style={{ margin: "0 0 6px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 8,
          }}
        >
          {/* A month switch reports itself here rather than behind a dimmer over
              the table: the rows it's replacing stay readable, and a spinner
              next to the count is where the count is about to change. */}
          <Header as="h4" style={{ margin: 0, whiteSpace: "nowrap" }}>
            {activeLabel} Bookings{" "}
            <Label circular>{listBookings.length}</Label>
            {/* Always rendered, only made invisible: a spinner that appears and
                disappears from the flow would shove the table down and back on
                every month switch. Wrapped in a span because Semantic styles a
                header's direct child icon as an icon-header — display:table-cell,
                which drops it onto a line of its own. */}
            <span style={{ marginLeft: 6, visibility: listLoading ? "visible" : "hidden" }}>
              <Icon name="spinner" loading />
            </span>
          </Header>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <Menu compact size="small">
              {BOOKING_TYPE_OPTIONS.map((o) => (
                <Menu.Item
                  key={o.value}
                  active={bookingTypeFilter === o.value}
                  onClick={() => onFilterChange({ btype: o.value })}
                >
                  {o.label}
                </Menu.Item>
              ))}
            </Menu>
            {/* Day shortcuts. The caption names the field they match, which
                follows the selected stat tile (see dayKeyFor) — without it
                "Today" would be ambiguous the moment the tile changes. There's
                no "All" item: clicking the active chip clears it, and a second
                "All" next to the type menu's would read ambiguously. */}
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              {/* Yesterday/Today/Tomorrow all fall outside a past or future
                  month's fetched rows, where they'd only ever empty the list —
                  so they show for All Time and the current month alone. Reset
                  stays put either way, since it clears more than the chips. */}
              {(month === ALL_TIME || month === currentMonth) && (
                <>
                  <span style={{ color: "rgba(0,0,0,.5)", fontSize: "0.9em" }}>
                    {DAY_FIELD_LABEL[activeKey] || "Arrival"}:
                  </span>
                  <Menu compact size="small">
                    {dayOptions.map((o) => (
                      <Menu.Item
                        key={o.value}
                        active={dayFilter === o.value}
                        disabled={o.disabled}
                        title={o.disabled ? o.disabledHint : `Show ${o.label.toLowerCase()}`}
                        onClick={() => onFilterChange({ day: dayFilter === o.value ? "" : o.value })}
                      >
                        {o.label}
                      </Menu.Item>
                    ))}
                  </Menu>
                </>
              )}
              {/* Clears the row's filters — chips, type, the Filters panel's
                  dates and the search box — without leaving the stat tile
                  you're viewing. Kept visible-but-disabled rather than
                  conditional so the row doesn't reflow on every pick. */}
              <Button
                type="button"
                basic
                size="small"
                disabled={listFilterCount === 0}
                onClick={clearListFilters}
                title="Clear the filters on this list"
              >
                <Icon name="undo" />
                Reset
              </Button>
            </div>
            <Input
              icon="search"
              iconPosition="left"
              placeholder="Search any column…"
              value={listSearch}
              onChange={(e) => setListSearch(e.target.value)}
              action={
                listSearch
                  ? { icon: "times circle", basic: true, onClick: () => setListSearch("") }
                  : undefined
              }
            />
            <Button type="button" toggle active={filterOpen} onClick={() => setFilterOpen((o) => !o)} title="Filters">
              <Icon name="filter" />
              Filters
              {activeFilterCount > 0 && (
                <Label circular color="blue" size="mini" style={{ marginLeft: 6 }}>
                  {activeFilterCount}
                </Label>
              )}
            </Button>
            <Dropdown
              button
              className="icon red"
              disabled={exporting}
              icon={null}
              /* `trigger`, not `text` — same reason as MonthDropdown above:
                 Semantic UI types `text` as a string. */
              trigger={<Icon name={exporting ? "spinner" : "file pdf outline"} loading={exporting} fitted />}
              title="Download today's & tomorrow's occupancy list (PDF) — every room in every guest house, ignoring the filters on this table"
            >
              <Dropdown.Menu direction="left">
                <Dropdown.Item icon="tint" text="Coloured" onClick={() => handleExport("color")} />
                <Dropdown.Item icon="print" text="Black & white" onClick={() => handleExport("bw")} />
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>

        {exportError && (
          <Message
            negative
            size="small"
            icon="warning circle"
            style={{ marginTop: 8 }}
            header="Couldn't download the occupancy list"
            content={exportError}
            onDismiss={() => setExportError("")}
          />
        )}

        {filterOpen && (
          <Segment secondary>
            <Form>
              <Form.Group widths="equal">
                <Form.Input
                  label="Arrival Date"
                  type="date"
                  value={colArrival}
                  onChange={(e) => onFilterChange({ colArrival: e.target.value })}
                />
                <Form.Input
                  label="Departure Date"
                  type="date"
                  value={colDeparture}
                  onChange={(e) => onFilterChange({ colDeparture: e.target.value })}
                />
                <Form.Select
                  label="Status"
                  value={activeKey}
                  options={stats.map((s) => ({ key: s.key, value: s.key, text: s.label }))}
                  onChange={(_, data) => selectStatus(data.value)}
                />
              </Form.Group>
              {activeFilterCount > 0 && (
                <Button type="button" basic size="small" onClick={resetFilters}>
                  <Icon name="times" /> Clear Filters
                </Button>
              )}
            </Form>
          </Segment>
        )}
      </div>

      <DataTable
        key={filterKey}
        data={listBookings}
        columns={columns}
        batchSize={25}
        anchorRow={firstCurrentArrival}
        emptyMessage={
          month === ALL_TIME
            ? `No ${activeLabel.toLowerCase()} bookings found.`
            : `No ${activeLabel.toLowerCase()} bookings arriving in ${monthLabel(month)}.`
        }
        getRowStyle={rowStyleFor}
        onRowClick={onOpenDetail}
        minWidth={1050}
        fullBleed
      />
    </div>
  );
}
